// Winning Formula — "what actually decides your games."
//
// Splits team-level per-game stats by the team's OWN wins vs losses and ranks
// the gaps. No league baselines: the team is its own control group. A metric
// only qualifies when it has data in ≥2 wins AND ≥2 losses (story stats are
// absent on pre-2026-07 imports, so each metric qualifies independently).
//
// Pure + framework-free. Consumes raw game docs (game.result + game.stats).

import { ipToInnings, normalizeRate } from './season-stats';
import type { StatKind } from './stat-format';

export interface FormulaGap {
  key: string;
  label: string;
  /** Which direction is good — drives W/L color + copy */
  better: 'higher' | 'lower';
  winsValue: number;
  lossesValue: number;
  /** Relative gap 0–1, used for ranking */
  gapScore: number;
  kind: StatKind;
  winGames: number;
  lossGames: number;
}

interface TeamGame {
  outcome: 'W' | 'L';
  // per-game team sums
  pa: number; ab: number; h: number; bb: number; so: number; sb: number;
  qab: number | null; ps: number | null;
  opsSum: number; opsW: number;
  ipTrue: number; bbAllowed: number; pitchesThrown: number;
  fpsSum: number; fpsW: number; sSum: number; sW: number;
  runsFor: number; runsAgainst: number;
}

function teamGame(game: any): TeamGame | null {
  const outcome = game?.result?.outcome;
  if (outcome !== 'W' && outcome !== 'L') return null;
  if (!game.stats) return null;

  const t: TeamGame = {
    outcome, pa: 0, ab: 0, h: 0, bb: 0, so: 0, sb: 0, qab: null, ps: null,
    opsSum: 0, opsW: 0, ipTrue: 0, bbAllowed: 0, pitchesThrown: 0,
    fpsSum: 0, fpsW: 0, sSum: 0, sW: 0,
    runsFor: game.result.myScore ?? 0, runsAgainst: game.result.opponentScore ?? 0,
  };

  Object.values(game.stats).forEach((s: any) => {
    if (s.pa > 0 || s.ab > 0) {
      t.pa += s.pa || 0; t.ab += s.ab || 0; t.h += s.h || 0;
      t.bb += s.bb || 0; t.so += s.so || 0; t.sb += s.sb || 0;
      if (s.qab != null) t.qab = (t.qab ?? 0) + s.qab;
      if (s.ps != null) t.ps = (t.ps ?? 0) + s.ps;
      if (s.ops != null) { t.opsSum += s.ops * (s.pa || 1); t.opsW += s.pa || 1; }
    }
    if (s.ip > 0 || s.pitches > 0) {
      t.ipTrue += ipToInnings(s.ip);
      t.bbAllowed += s.bbAllowed || 0;
      t.pitchesThrown += s.pitches || 0;
      const fps = normalizeRate(s.fpsPct);
      const sp = normalizeRate(s.sPct);
      const bfW = s.bf || Math.round((s.ip || 0) * 3) || 1;
      if (fps != null) { t.fpsSum += fps * bfW; t.fpsW += bfW; }
      if (sp != null) { t.sSum += sp * (s.pitches || 1); t.sW += s.pitches || 1; }
    }
  });
  return t;
}

interface MetricDef {
  key: string; label: string; better: 'higher' | 'lower'; kind: StatKind;
  /** Skip the metric when max(|W avg|,|L avg|) is below this — a 0.4-SB-per-game
   *  "gap" is 100% relative but meaningless; don't let it outrank real stories. */
  minMagnitude?: number;
  value: (g: TeamGame) => number | null;
}

const METRICS: MetricDef[] = [
  { key: 'bbInnAllowed', label: 'Walks allowed per inning', better: 'lower', kind: 'ratio1',
    value: g => g.ipTrue > 0 ? g.bbAllowed / g.ipTrue : null },
  { key: 'qabPct', label: 'Quality at-bat rate', better: 'higher', kind: 'pct',
    value: g => (g.qab != null && g.pa > 0) ? g.qab / g.pa : null },
  { key: 'fpsPct', label: 'First-pitch strikes', better: 'higher', kind: 'pct',
    value: g => g.fpsW > 0 ? g.fpsSum / g.fpsW : null },
  { key: 'sPct', label: 'Strike percentage', better: 'higher', kind: 'pct',
    value: g => g.sW > 0 ? g.sSum / g.sW : null },
  { key: 'kRate', label: 'Strikeout rate (batting)', better: 'lower', kind: 'pct',
    value: g => g.pa > 0 ? g.so / g.pa : null },
  { key: 'ops', label: 'Team OPS', better: 'higher', kind: 'avg3',
    value: g => g.opsW > 0 ? g.opsSum / g.opsW : null },
  { key: 'sbPerGame', label: 'Stolen bases', better: 'higher', kind: 'ratio1', minMagnitude: 1.5,
    value: g => g.sb },
  { key: 'bbDrawn', label: 'Walks drawn', better: 'higher', kind: 'ratio1', minMagnitude: 2,
    value: g => g.bb },
];

export interface WinningFormulaResult {
  gaps: FormulaGap[];
  wins: number;
  losses: number;
  /** false when the season lacks ≥2 wins and ≥2 losses with stats */
  hasEnoughGames: boolean;
}

export function computeWinningFormula(games: any[]): WinningFormulaResult {
  const teamGames = games.map(teamGame).filter((g): g is TeamGame => g != null);
  const wins = teamGames.filter(g => g.outcome === 'W');
  const losses = teamGames.filter(g => g.outcome === 'L');

  const result: WinningFormulaResult = {
    gaps: [], wins: wins.length, losses: losses.length,
    hasEnoughGames: wins.length >= 2 && losses.length >= 2,
  };
  if (!result.hasEnoughGames) return result;

  METRICS.forEach(m => {
    const wVals = wins.map(m.value).filter((v): v is number => v != null);
    const lVals = losses.map(m.value).filter((v): v is number => v != null);
    if (wVals.length < 2 || lVals.length < 2) return;

    const wAvg = wVals.reduce((a, b) => a + b, 0) / wVals.length;
    const lAvg = lVals.reduce((a, b) => a + b, 0) / lVals.length;
    const denom = Math.max(Math.abs(wAvg), Math.abs(lAvg));
    if (denom === 0) return;
    if (m.minMagnitude != null && denom < m.minMagnitude) return;

    result.gaps.push({
      key: m.key, label: m.label, better: m.better, kind: m.kind,
      winsValue: wAvg, lossesValue: lAvg,
      gapScore: Math.abs(wAvg - lAvg) / denom,
      winGames: wVals.length, lossGames: lVals.length,
    });
  });

  result.gaps.sort((a, b) => b.gapScore - a.gapScore);
  return result;
}
